import { EnvelopeIcon } from '@heroicons/react/20/solid';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import type { AsyncStatus } from '../../../@types/AsyncStatus';
import type { CoproAttributed } from '../../../@types/CoproAttributed';
import { AttributionRole, AttributionRoleLabels } from '../../../@types/CoproUserAttribution';
import { useAppDispatch } from '../../../store/hook';
import type { RootState } from '../../../store/store';
import { getValueByLabel } from '../../../utils/attributions';
import classNames from '../../../utils/classNames';
import { JWTAuthorization, JWTHeader } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import Badge from '../../common/Badge';
import { showToast } from '../../common/Notification';
import Popup from '../../common/Popup';
import { SetupPage, updateSetupState } from '../../common/SetupPage';
import Spinner from '../../common/Spinner';
import Table from '../../common/Table';

type RoleFilter = 'all' | AttributionRole;

const allRoles: Array<{ label: string; value: RoleFilter }> = [
  { label: 'TOUS LES RÔLES', value: 'all' },
  { label: AttributionRoleLabels[AttributionRole.REFERENT], value: AttributionRole.REFERENT },
  { label: AttributionRoleLabels[AttributionRole.ASSISTANT_REFERENT], value: AttributionRole.ASSISTANT_REFERENT },
  { label: AttributionRoleLabels[AttributionRole.GESTIONNAIRE], value: AttributionRole.GESTIONNAIRE },
  { label: AttributionRoleLabels[AttributionRole.ASSISTANT_GESTIONNAIRE], value: AttributionRole.ASSISTANT_GESTIONNAIRE },
  { label: AttributionRoleLabels[AttributionRole.TRANSACTIONNAIRE], value: AttributionRole.TRANSACTIONNAIRE },
  { label: AttributionRoleLabels[AttributionRole.GESTIONNAIRE_LOCATIF], value: AttributionRole.GESTIONNAIRE_LOCATIF },
  { label: AttributionRoleLabels[AttributionRole.INTERVENANT], value: AttributionRole.INTERVENANT },
];

const sortableColumns = {
  'N° IMMAT': 'string', 'NOM DE LA COPROPRIÉTÉ': 'string', NOM: 'string', ADRESSE: 'string', MANDANT: 'string',
};

function ActivitySetupStepNotification() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);

  const roles = useMemo(() => allRoles, []);

  const [copros, setCopros] = useState<CoproAttributed[]>([]);
  const [status, setStatus] = useState<AsyncStatus>('IDLE');
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isPopupOpen, setIsPopupOpen] = useState<boolean | string>(false);
  const [isBatchLoading, setIsBatchLoading] = useState<boolean>(false);
  const [errorMessage, setError] = useState<string | null>(null);
  const [globalNotification, setGlobalNotification] = useState<boolean | null>(null);
  const [collabGlobalNotification, setCollabGlobalNotification] = useState<boolean | null>(null);
  const [selectedRole, setSelectedRole] = useState<RoleFilter | null>(null);
  const [selectedCollab, setSelectedCollab] = useState<number | string | null>(null);
  const [isCoproDataLoaded, setIsCoproDataLoaded] = useState(false);
  const [isTop, setIsTop] = useState(true);
  const [isBottom, setIsBottom] = useState(false);
  const topRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    const topObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsTop(true);
        } else if (!entry.isIntersecting) {
          setIsTop(false);
        }
      },
      { threshold: 1 },
    );

    const bottomObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsBottom(true);
        } else if (!entry.isIntersecting) {
          setIsBottom(false);
        }
      },
      { threshold: 0 },
    );

    if (topRef.current) {
      topObserver.observe(topRef.current);
    }

    if (bottomRef.current) {
      bottomObserver.observe(bottomRef.current);
    }

    return () => {
      if (topRef.current) {
        topObserver.unobserve(topRef.current);
      }
      if (bottomRef.current) {
        bottomObserver.unobserve(bottomRef.current);
      }
    };
  }, [topRef, bottomRef]);

  const fetchAttributions = async() => {
    try {
      const response = await fetch(`/api/companies/${company.id}/validated-copros/attributions`, JWTHeader(jwt));
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching attributions :', error);
      showToast('error', 'Une erreur est survenue lors du chargement des données.');
      setError('Erreur lors du chargement des attributions.');
    }
  };

  const fetchCopros = async() => {
    try {
      const response = await fetch(`/api/companies/${company.id}/copros?limit=10000&filterValidatedByUser=true`, JWTHeader(jwt));
      const result = await response.json();
      const coprosResult = result.results;

      const attributions = await fetchAttributions();

      const coprosWithAttributions = coprosResult.map((copro) => ({
        ...copro,
        attributions: attributions.filter((attr) => attr.copro.id == copro.id).map((attribution) => ({
          ...attribution,
          label: attribution.user ? `${attribution.user.firstname} ${attribution.user.lastname ? attribution.user.lastname.toUpperCase() : ''} (${attribution.user.email})` : '',
        })),
      }));

      // Sort copros so those with MANDANT are last
      const sortedCopros = coprosWithAttributions.sort((a, b) => {
        const aHasMandant = a.company.id !== company.id && a.company.subcontractors.some((subcontractor) => subcontractor.id === company.id);
        const bHasMandant = b.company.id !== company.id && b.company.subcontractors.some((subcontractor) => subcontractor.id === company.id);
        return aHasMandant - bHasMandant;
      });

      setCopros(sortedCopros);
      setStatus('SUCCESS');
      setIsCoproDataLoaded(true);
    } catch (error) {
      console.error('Error fetching copros :', error);
      showToast('error', 'Une erreur est survenue lors du chargement des données.');
      setStatus('ERROR');
      setError('Erreur lors du chargement des copropriétés');
    }
  };

  useEffect(() => {
    if (jwt && company && company.id && status !== 'PENDING') {
      fetchCopros();
    }
  }, [jwt, company]);

  const toggleNotification = async(attributionId: number, isNotified: boolean, isSingle = false) => {
    try {
      const endpoint = isNotified
        ? `/api/attributions/${attributionId}/no-dpe-notifier`
        : `/api/attributions/${attributionId}/dpe-notifier`;

      const response = await fetch(endpoint, {
        method: 'PATCH',
        headers: {
          Authorization: JWTAuthorization(jwt),
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Error updating attribution');
      } else if (isSingle) {
        if (isNotified) {
          showToast('success', 'Ce collaborateur a été désabonné des notifications.');
        } else {
          showToast('success', 'Ce collaborateur est désormais abonné aux notifications.');
        }
      }
    } catch (error) {
      console.error('Error toggling notification :', error);
      showToast('error', 'Une erreur est survenue lors de la mise à jour de la notification.');
      setError('Erreur lors de la mise à jour de la notification.');
    }
  };

  const handleSingleToggleNotification = async(coproIdx: number, attributionIdx: number, attributionId: number, isNotified: boolean) => {
    const coprosTmp = [...copros];
    const attribution = coprosTmp[coproIdx].attributions[attributionIdx];
    attribution.toNotifyForNewDPE = !isNotified;
    coprosTmp[coproIdx].attributions[attributionIdx] = attribution;
    setCopros(coprosTmp);
    toggleNotification(attributionId, isNotified, true);
    await fetchCopros();
  };

  const handleBatchAssign = async() => {
    if (globalNotification !== null && selectedRole !== null) {
      setIsBatchLoading(true);
      const promises = selectedRows.map(async(coproId) => {
        const copro = copros.find((c) => c.id === coproId);
        if (copro) {
          let attributionIds: number[] = [];
          if (selectedRole === 'all') {
            attributionIds = copro.attributions.map((attr) => attr.id);
          } else {
            attributionIds = copro.attributions
              .filter((attr) => attr.fonctionType === selectedRole)
              .map((attr) => attr.id);
          }
          return Promise.all(attributionIds.map((id) => toggleNotification(id, globalNotification)));
        }
      });

      await Promise.all(promises);
      await fetchCopros();
      setIsPopupOpen(false);
      setIsBatchLoading(false);
    }
  };

  const handleCollabBatchAssign = async() => {
    if (collabGlobalNotification !== null && selectedCollab !== null) {
      setIsBatchLoading(true);
      let attributionIds = [];
      if (selectedCollab === 'all') {
        attributionIds = copros
          .flatMap((copro) => copro.attributions)
          .map((attr) => attr.id);
      } else {
        attributionIds = copros
          .flatMap((copro) => copro.attributions)
          .filter((attr) => attr.user.id === selectedCollab)
          .map((attr) => attr.id);
      }

      await Promise.all(attributionIds.map((id) => toggleNotification(id, collabGlobalNotification)));
      await fetchCopros();
      setIsPopupOpen(false);
      setIsBatchLoading(false);
    }
  };

  const tableData = copros.map((copro, coproIdx) => {
    const row = {
      'N° IMMAT': copro.remoteId, 'NOM DE LA COPROPRIÉTÉ': copro.name, id: copro.id, ADRESSE: '', MANDANT: <></>, rawData: {},
    };
    if (copro.addresses.length > 0) {
      row.ADRESSE = `${copro.addresses[0].address} ${copro.addresses[0].postalCode} ${copro.addresses[0].city}`;
    }
    const isSubcontracted = copro.company.id !== company.id && copro.company.subcontractors.some((subcontractor) => subcontractor.id === company.id);
    row.rawData = copro;
    row.MANDANT = isSubcontracted
      ? (
        <div className="text-xs text-black">
          {copro.company.name}
        </div>
      ) : <></>;

    roles.forEach((role) => {
      if (role.value === AttributionRole.INTERVENANT) {
        const users = copro.attributions ? copro.attributions.filter((a) => a.fonctionType === role.value) : [];
        row[role.label] = (
          <div className="flex flex-col gap-1">
            {users && users.length > 0 && users.map((user, idx) => (
              <div key={`${copro.id}-interv-${user.id}-${idx}`} className="flex items-center justify-between">
                <span>{user.label}</span>
                <EnvelopeIcon
                  className={`cursor-pointer ${user.toNotifyForNewDPE ? 'text-green-500' : 'text-gray-500'} h-5 w-5`}
                  onClick={() => handleSingleToggleNotification(coproIdx, copro.attributions.findIndex((a) => a.id === user.id), user.id, user.toNotifyForNewDPE)}
                />
              </div>
            ))}
          </div>
        );
      } else if (role.value !== 'all') {
        const user = copro.attributions ? copro.attributions.find((attribution) => attribution.fonctionType === role.value) : null;
        const attributionIdx = copro.attributions ? copro.attributions.findIndex((attribution) => attribution.fonctionType === role.value) : null;
        row[role.label] = (
          <>
            {user && (
              <div className="flex justify-around">
                {user.label}
                <div className="flex flex-col justify-center">
                  <EnvelopeIcon
                    className={`cursor-pointer ${user.toNotifyForNewDPE ? 'text-green-500' : 'text-gray-500'} h-5 w-5`}
                    onClick={() => handleSingleToggleNotification(coproIdx, attributionIdx, user.id, user.toNotifyForNewDPE)}
                  />
                </div>
              </div>
            )}
          </>
        );
      }
    });
    return row;
  });

  const headers = ['N° IMMAT', 'NOM DE LA COPROPRIÉTÉ', 'ADRESSE', ...roles.filter((role) => role.value !== 'all').map((role) => role.label), 'MANDANT'];

  const handleTableDataChange = (newVal: boolean, id: string) => {
    const numericId = Number(id);
    if (selectedRows.includes(numericId) && newVal === false) {
      setSelectedRows(selectedRows.filter((row) => row !== numericId));
    }
    if (!selectedRows.includes(numericId) && newVal === true) {
      setSelectedRows([...selectedRows, numericId]);
    }
  };

  const handleSelectAll = () => {
    const allSelected = selectedRows.length === copros.length;
    if (allSelected) {
      setSelectedRows([]);
    } else {
      setSelectedRows(copros.map((copro) => copro.id));
    }
  };

  const handleNextStep = async() => {
    // make sure we are not loading the data before we can navigate
    if (status !== 'SUCCESS' || isBatchLoading) {
      return;
    }
    await updateSetupState(company, jwt, 'mailAlertSetup', dispatch);
    navigate(ROUTES.SETUP_SIMPLIFIED_DASHBOARD);
  };

  const renderSaveButton = () => (
    <div
      className={classNames(
        'text-right w-full md:w-auto mt-4',
        !isTop && !isBottom && 'fixed z-[1000] right-0 bottom-0 md:right-5 md:bottom-5',
      )}
    >
      <button
        onClick={handleNextStep}
        className={classNames(
          'bg-rgpd-green text-white text-xl px-8 py-4 shadow-md mt-6 align-center w-full md:w-auto',
          (status === 'PENDING' || status === 'IDLE') && 'opacity-80 cursor-not-allowed',
          (isTop || isBottom) ? 'rounded' : 'md:rounded',
        )}
      >
        Valider ma configuration
      </button>
    </div>
  );

  const mergedCellsConfig = (item: { rawData: CoproAttributed; id: number }, index: number) => {
    const config: { [header: string]: { colSpan: number; content: string | JSX.Element } } = {};

    if (item.rawData.company && item.rawData.company.subcontractors) {
      const shouldMerge = item.rawData.company.subcontractors.some((subcontractor) => subcontractor.id === company.id);

      if (shouldMerge && item && item.rawData && item.rawData.attributions) {
        const users = item.rawData.attributions ? item.rawData.attributions.filter((attribution) => attribution.fonctionType === getValueByLabel('INTERVENANT EXTÉRIEUR')) : [];
        const coproUsersAttributed = users && users.length > 0 ? (
          <div className="flex flex-col items-center gap-1">
            {users.map((user) => (
              <div key={`${item.id}-interv-${user.id}`} className="flex items-center">
                {user.label}
                <div className="ml-2 flex flex-col justify-center">
                  <EnvelopeIcon
                    className={`cursor-pointer ${user.toNotifyForNewDPE ? 'text-green-500' : 'text-gray-500'} h-5 w-5`}
                    onClick={() => handleSingleToggleNotification(index, item.rawData.attributions.findIndex((a) => a.id === user.id), user.id, user.toNotifyForNewDPE)}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : <div className="text-center">Cette copropriété n'a pas encore de responsable.</div>;

        config['GESTIONNAIRE DE L\'IMMEUBLE'] = { colSpan: 5, content: coproUsersAttributed };
        config.TRANSACTIONNAIRE = { colSpan: 4, content: '' };
        config['GESTIONNAIRE LOCATIF'] = { colSpan: 4, content: '' };
        config['RESPONSABLE DU DOCUMENT D\'AG'] = { colSpan: 4, content: '' };
        config['INTERVENANT EXTÉRIEUR'] = { colSpan: 4, content: '' };
      }
    }

    return config;
  };

  return (
    <SetupPage
      step={'mailAlertSetup'}
      title="CONFIGURER LES NOTIFICATIONS POUR CHAQUE COPROPRIÉTÉ / ASL / IMMEUBLE TERTIAIRE"
      children={(
        <>
          <div ref={topRef} style={{ height: '1px' }} />
          
          <div className="mb-8 rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
             <p className="mb-6 text-lg leading-relaxed font-medium text-gray-800">
                La dernière étape du paramétrage consiste à définir les paramètres de notifications par email pour les nouveaux DPEs. Vous pouvez activer ou désactiver les notifications pour chaque copropriété, et pour chaque collaborateur.
            </p>
             <p className="mb-6 text-gray-600">
                Définissez les préférences de notification pour chaque copropriété afin{' '}
                <span className="font-medium text-rgpd-blue-light">d'assurer que les bonnes personnes reçoivent les informations nécessaires</span>
                {' '}
                à la qualification des nouveaux DPEs.
            </p>
            
            <div className="inline-block rounded-lg border border-gray-100 bg-gray-50 p-4">
                <div className="mb-2 text-sm font-medium text-gray-700">Cliquez sur l'enveloppe pour activer ou désactiver les notifications</div>
                <div className="flex gap-6">
                    <div className="flex items-center text-sm">
                         <EnvelopeIcon className="mr-2 h-5 w-5 text-green-500" />
                         <span>Notification mail activée</span>
                    </div>
                    <div className="flex items-center text-sm">
                        <EnvelopeIcon className="mr-2 h-5 w-5 text-gray-500" />
                         <span>Notification mail désactivée</span>
                    </div>
                </div>
            </div>

            <div className="mt-8 border-t border-gray-100 pt-6">
              <h2 className="flex flex-col items-start gap-3 text-xl font-bold text-gray-800 md:flex-row md:items-center">
                <span className="rounded bg-rgpd-blue-light px-3 py-1 text-sm tracking-wide whitespace-nowrap text-white uppercase">ÉTAPE N°6 :</span>
                <span>CONFIGURER LES NOTIFICATIONS POUR CHAQUE COPROPRIÉTÉ / ASL / IMMEUBLE TERTIAIRE</span>
              </h2>
            </div>
          </div>

          <h2 className="mt-12 mb-6 text-center text-xl font-semibold text-rgpd-blue-light uppercase">LISTE DES COPROPRIÉTÉS ET CONFIGURATION DES NOTIFICATIONS</h2>
          {(status === 'PENDING' || status === 'IDLE') && (
            <p className="mx-10 my-32 text-center">
              <Spinner aria-label="Center-aligned spinner" size="xl" />
            </p>
          )}
          {status === 'SUCCESS' && copros.length > 0 && (
            <div className="overflow-x-visible">
              <div className="items-center justify-between md:flex md:flex-wrap">
                <div className="md:flex">
                  <button onClick={handleSelectAll} className="mx-auto mr-4 mb-4 w-full rounded px-4 py-2 text-rgpd-blue-light outline-1 outline-rgpd-blue-light md:w-auto">
                    {selectedRows.length === copros.length ? 'Désélectionner tout' : 'Sélectionner tout'}
                  </button>
                  <button 
                    onClick={() => setIsPopupOpen('role')} 
                    className={`mx-auto mr-4 mb-4 w-full rounded bg-rgpd-blue-light px-4 py-2 text-white md:w-auto ${selectedRows.length === 0 ? 'cursor-not-allowed opacity-50' : ''}`}
                    disabled={selectedRows.length === 0}
                  >
                    Editer les notifications des
                    <br />
                    {' '}
                    copropriétés sélectionnées
                  </button>
                </div>
                <div>
                  <button onClick={() => setIsPopupOpen('collab')} className="mx-auto mb-4 w-full rounded bg-rgpd-blue-dark px-4 py-2 text-white md:w-auto">
                    Editer toutes les notifications
                    <br />
                    {' '}
                    d'un collaborateur
                  </button>
                </div>
              </div>
              <Table
                isSearchable
                sortableColumns={sortableColumns}
                data={tableData.map((row) => ({ ...row, VALIDE: selectedRows.includes(row.id) }))}
                headers={headers}
                tableClass="min-w-full bg-white border text-sm"
                columnChecking
                idKey="id"
                dataChangeCallback={handleTableDataChange}
                validColumnLabel="#"
                mergedCellsConfig={mergedCellsConfig}
                stickyHeader
              />
              <div className="mb-4 items-center justify-between md:flex md:flex-wrap">
                <div className="md:flex">
                  <button onClick={handleSelectAll} className="mx-auto mt-4 mr-4 w-full rounded px-4 py-2 text-rgpd-blue-light outline-1 outline-rgpd-blue-light md:w-auto">
                    {selectedRows.length === copros.length ? 'Désélectionner tout' : 'Sélectionner tout'}
                  </button>
                  <button 
                    onClick={() => setIsPopupOpen('role')} 
                    className={`mx-auto mt-4 mr-4 w-full rounded bg-rgpd-blue-light px-4 py-2 text-white md:w-auto ${selectedRows.length === 0 ? 'cursor-not-allowed opacity-50' : ''}`}
                    disabled={selectedRows.length === 0}
                  >
                    Editer les notifications des
                    <br />
                    {' '}
                    copropriétés sélectionnées
                  </button>
                </div>
                <div>
                  <button onClick={() => setIsPopupOpen('collab')} className="mx-auto mt-4 w-full rounded bg-rgpd-blue-dark px-4 py-2 text-white md:w-auto">
                    Editer toutes les notifications
                    <br />
                    {' '}
                    d'un collaborateur
                  </button>
                </div>
              </div>
            </div>
          )}
          {status === 'SUCCESS' && copros.length === 0 && (
            <div className="w-full bg-white pb-2">
              <Table
                data={[]}
                headers={headers}
                tableClass="text-left mb-3 text-sm"
                validColumnLabel="#"
                isSearchable
              />
              <div className="flex w-full place-content-center">
                <Badge
                  mode="warning"
                  text="Aucune copropriété trouvée"
                  className="my-3"
                />
              </div>
            </div>
          )}
          {status === 'ERROR' && (
            <Popup
              isOpen={!!errorMessage}
              onClose={() => setError(null)}
              title="Erreur"
              children={(
                <Badge
                  mode="error"
                  text={errorMessage}
                />
              )}
            />
          )}
          {/* must be on top of the button otherwise the button appearance makes it dissapear */}
          <div ref={bottomRef} style={{ height: '1px' }} />
          {isCoproDataLoaded && renderSaveButton()}
          <Popup
            isOpen={isPopupOpen === 'role'}
            onClose={() => setIsPopupOpen(false)}
            title="Changement des notifications en lot"
            disableCloseButton={isBatchLoading}
            children={(
              <div>
                {isBatchLoading ? (
                  <p className="mx-10 my-32 text-center">
                    <Spinner aria-label="Center-aligned spinner" size="xl" />
                  </p>
                ) : (
                  <>
                    <div className="my-3">
                      <div className="mb-2 font-semibold">Rôle :</div>
                      <select
                        value={selectedRole || ''}
                        onChange={(e) => setSelectedRole(e.target.value === '' ? null : (e.target.value as RoleFilter))}
                        className="mb-2 w-full md:w-auto"
                      >
                        <option value="">Sélectionner un rôle</option>
                        {roles.map((role) => (
                          <option key={role.value} value={role.value}>{role.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="my-3">
                      <label>
                        <input
                          type="radio"
                          value="false"
                          checked={globalNotification === false}
                          onChange={() => setGlobalNotification(false)}
                          className="mr-2 mb-1 ml-1"
                        />
                        Activer les notifications
                      </label>
                    </div>
                    <div className="my-3">
                      <label>
                        <input
                          type="radio"
                          value="true"
                          checked={globalNotification === true}
                          onChange={() => setGlobalNotification(true)}
                          className="mr-2 mb-1 ml-1"
                        />
                        Désactiver les notifications
                      </label>
                    </div>
                    <center>
                      <button
                        onClick={handleBatchAssign}
                        className="mt-4 w-full rounded bg-rgpd-blue-light px-4 py-2 text-white md:w-auto"
                        disabled={selectedRole === null}
                      >
                        Appliquer
                      </button>
                    </center>
                  </>
                )}
              </div>
            )}
          />
          <Popup
            isOpen={isPopupOpen === 'collab'}
            onClose={() => setIsPopupOpen(false)}
            title="Changement des notifications par collaborateur"
            disableCloseButton={isBatchLoading}
            children={(
              <div>
                {isBatchLoading ? (
                  <p className="mx-10 my-32 text-center">
                    <Spinner aria-label="Center-aligned spinner" size="xl" />
                  </p>
                ) : (
                  <>
                    <div className="my-3">
                      <div className="mb-2 font-semibold">Collaborateur :</div>
                      <select
                        value={selectedCollab || ''}
                        onChange={(e) => setSelectedCollab(e.target.value === 'all' ? 'all' : Number(e.target.value))} // Update to handle 'all'
                        className="mb-2 w-full md:w-auto"
                      >
                        <option value="">Sélectionner un collaborateur</option>
                        {copros
                          .flatMap((copro) => copro.attributions.map((attr) => attr.user))
                          .filter((user, index, self) => self.findIndex((u) => u.id === user.id) === index)
                          .sort((a, b) => a.lastname.localeCompare(b.lastname))
                          .map((user) => (
                            <option key={user.id} value={user.id}>{`${user.firstname} ${user.lastname ? user.lastname.toUpperCase() : ''} (${user.email})`}</option>
                          ))}
                      </select>
                    </div>
                    <div className="my-3">
                      <label>
                        <input
                          type="radio"
                          value="false"
                          checked={collabGlobalNotification === false}
                          onChange={() => setCollabGlobalNotification(false)}
                          className="mr-2 mb-1 ml-1"
                        />
                        Activer les notifications
                      </label>
                    </div>
                    <div className="my-3">
                      <label>
                        <input
                          type="radio"
                          value="true"
                          checked={collabGlobalNotification === true}
                          onChange={() => setCollabGlobalNotification(true)}
                          className="mr-2 mb-1 ml-1"
                        />
                        Désactiver les notifications
                      </label>
                    </div>
                    <center>
                      <button
                        onClick={handleCollabBatchAssign}
                        className="mt-4 w-full rounded bg-rgpd-blue-light px-4 py-2 text-white md:w-auto"
                        disabled={selectedCollab === null}
                      >
                        Appliquer
                      </button>
                    </center>
                  </>
                )}
              </div>
            )}
          />
        </>
      )}
    />
  );
}

export default ActivitySetupStepNotification;